// externals
import { uniq } from 'lodash';

// libraries
import type { SciVersionCommentChangeType } from '@makemydeal/dr-shared-types';
import { SCI_VERSION_COMMENT_LABELS } from '@makemydeal/dr-shared-types';

// utils
import { DEALER_REVIEW_FIELD_LABELS, getChangedFieldLabels } from './changedFieldLabels';

const CUSTOMER_DETAILS_LABEL = 'Customer Details';

const DESCRIPTOR_BY_CHANGE_TYPE: Record<SciVersionCommentChangeType, string> = {
    vehicle: DEALER_REVIEW_FIELD_LABELS.vehicleOfInterest,
    trade: DEALER_REVIEW_FIELD_LABELS.tradeIn,
    customer: CUSTOMER_DETAILS_LABEL
};

const CHANGE_TYPE_BY_COMMENT_LABEL = new Map<string, SciVersionCommentChangeType>(
    (Object.keys(DESCRIPTOR_BY_CHANGE_TYPE) as SciVersionCommentChangeType[]).map((changeType) => [
        SCI_VERSION_COMMENT_LABELS[changeType].toLowerCase(),
        changeType
    ])
);

export type SciVersionComment = {
    sender: string;
    descriptor?: string;
    freeText?: string;
};

const readChangeType = (body: string): SciVersionCommentChangeType | undefined =>
    CHANGE_TYPE_BY_COMMENT_LABEL.get(body.trim().toLowerCase());

export const parseSciVersionComment = (versionComment?: string): SciVersionComment | undefined => {
    const comment = (versionComment ?? '').trim();

    if (!comment) {
        return undefined;
    }

    const lastColon = comment.lastIndexOf(':');
    const boilerplateChangeType = lastColon === -1 ? undefined : readChangeType(comment.slice(lastColon + 1));

    if (boilerplateChangeType) {
        return { sender: comment.slice(0, lastColon).trim(), descriptor: DESCRIPTOR_BY_CHANGE_TYPE[boilerplateChangeType] };
    }

    const firstColon = comment.indexOf(':');

    if (firstColon === -1) {
        return { sender: '', freeText: comment };
    }

    return { sender: comment.slice(0, firstColon).trim(), freeText: comment.slice(firstColon + 1).trim() };
};

export type SciUpdateSummary = {
    labels: string[];
    sender: string;
    freeTextComment: string;
};

export type SciUpdateSummaryArgs = {
    changedFields?: string[];
    versionComment?: string;
    accumulatedDescriptors?: string[];
};

export const buildSciUpdateSummary = ({
    changedFields,
    versionComment,
    accumulatedDescriptors = []
}: SciUpdateSummaryArgs): SciUpdateSummary => {
    const parsed = parseSciVersionComment(versionComment);
    const descriptors = parsed?.descriptor ? accumulatedDescriptors.concat(parsed.descriptor) : accumulatedDescriptors;
    const labels = uniq(getChangedFieldLabels(changedFields).concat(descriptors));

    const zipSupersedesCustomer = labels.includes(DEALER_REVIEW_FIELD_LABELS.zipCode);

    return {
        labels: zipSupersedesCustomer ? labels.filter((label) => label !== CUSTOMER_DETAILS_LABEL) : labels,
        sender: parsed?.sender ?? '',
        freeTextComment: parsed?.freeText ?? ''
    };
};
